<template>
    <div>
        <div class="row mt-2 mb-2 pl-3">
            <vs-checkbox v-model="useTemplate">{{ $t('editor.common.loadTemplate') }}</vs-checkbox>
        </div>

        <div class="card" v-if="useTemplate">
            <div class="card-body">
                <dynamic-dropdown :apiUrl="apiUrl"
                                  v-model="fieldTemplateId"
                                  :id="'fieldTemplate' + _uid"
                                  :name="'fieldTemplate' + _uid"
                                  :fieldName="$t('editor.common.fieldTemplate')"
                                  :filter="'contains'"
                                  :height="500"
                                  :showGoToSelection="false"
                                  @change="fieldTemplateDropdownChange">
                </dynamic-dropdown>
            </div>
            <div class="card-footer" v-if="fieldTemplateId">
                <div class="btn-group m-0">
                    <button type="button" class="btn btn-link btn-primary" :disabled="loading" @click="fieldTemplateDetails">
                        <i v-if="loading" class="fa fa-spinner fa-pulse fa-fw"></i>
                        <i v-else class="fa fa-eye"></i>
                        {{ $t('buttons.details.btnText')}}
                    </button>
                    <button type="button" class="btn btn-link btn-primary" :disabled="loading" @click="loadFieldTemplate">
                        <i v-if="loading" class="fa fa-spinner fa-pulse fa-fw"></i>
                        <i v-else class="fas fa-cloud-download-alt"></i>
                        {{ $t('buttons.doLoad.btnText')}}
                    </button>
                </div>
            </div>
        </div>

        <hr />

        <div v-if="showFieldCode && hasFieldCodeEditRights" class="card mb-2">
            <div class="card-body" v-if="fieldCodes.length > 0">
                <label class="text-muted" :for="'fieldCode' + _uid">{{ $t('editor.common.fieldCode') }}</label>
                <kendo-dropdownlist :id="'fieldCode' + _uid"
                                    :name="'fieldCode' + _uid"
                                    v-model="model.fieldCode"
                                    :clear-button="true"
                                    :data-source="fieldCodes"
                                    :data-text-field="'displayName'"
                                    :data-value-field="'code'"
                                    :filter="'contains'"
                                    :optionLabel="$t('choose')"
                                    :height="500"
                                    style="width: 100%;">
                </kendo-dropdownlist>

            </div>
        </div>

        <JsonFormatter v-model="details" :show.sync="showDetails"
                       :titleKey="'buttons.details.btnText'"
                       @onClose="onDetailsClose">
        </JsonFormatter>
    </div>
</template>

<script>
    import { EditorComponentModel } from '../../models/models';
    import DynamicDropdown from '../../components/dropdown/DynamicDropdown'
    import JsonFormatter from '../../components/common/json.formatter.modal'
    import { mapGetters } from 'vuex';

    export default {
        props: {
            value: {
                type: EditorComponentModel,
                required: true
            },
            isFormField: {
                type: Boolean,
                required: true
            },
            showFieldCode: {
                type: Boolean,
                default: true
            },
            isAuxRegisterField: {
                type: Boolean,
                required: false
            },
            isEventFieldCode: {
                type: Boolean,
                required: false
            },
        },
        components: {
            DynamicDropdown,
            JsonFormatter
        },
        data() {
            return {
                model: this.value,
                useTemplate: false,
                loading: false,
                fieldTemplateId: '',
                isRegisterField: '',
                details: '',
                showDetails: false,
                fieldCodes: []
            }
        },
        methods: {
            getFieldTemplate() {
                return new Promise((resolve, reject) => {
                    this.loading = true;

                    this.$http.get(`/api/dynamicRegisters/loadFieldTemplate`, {
                        params: { fieldId: this.fieldTemplateId, isRegisterField: this.isRegisterField }
                    }).then(result => {
                        resolve(result.data.model);
                    }).catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        })

                        reject(error);
                    }).then(() => { this.loading = false; });
                });
            },
            loadFieldTemplate() {
                this.getFieldTemplate()
                    .then(data => {
                        const resultModel = new EditorComponentModel(data);
                        this.$helpers.updateFieldTemplateObject(this.model, resultModel);
                    })
                    .catch(error => { });

                if (this.model.fieldType === 'Dropdown') {
                    // Do not load and select Droppdown members on template load.
                    // This is strange workaround
                    this.getFieldTemplate()
                        .then(data => {
                            const resultModel = new EditorComponentModel(data);
                            this.$helpers.updateFieldTemplateObject(this.model, resultModel);
                        })
                        .catch(error => { });
                }
            },
            loadFieldCodes() {
                this.$http.get(`/api/dynamicRegisters/loadFieldCodes`, { params: { isFormField: this.isFormField } })
                    .then(result => {
                        if (result.data) {
                            this.fieldCodes = result.data;
                        }
                    }).catch(error => {

                    });
            },
            fieldTemplateDetails() {
                this.getFieldTemplate()
                    .then(data => {
                        this.details = data;
                        this.showDetails = true;
                    })
                    .catch(error => {
                        this.details = '';
                        this.showDetails = false;
                    });
            },
            fieldTemplateDropdownChange(val, item) {
                if (Array.isArray(item) && item.length > 0) {
                    this.isRegisterField = item[0].isRegisterField;
                    return;
                }

                this.isRegisterField = item.isRegisterField;
            },
            onDetailsClose() {
                this.details = '';
                this.showDetails = false
            },
        },
        mounted() {
            this.loadFieldCodes();
        },
        computed: {
            ...mapGetters(['isEnVersion', 'claims']),
            hasFieldCodeEditRights() {
                if (this.isFormField) {
                    return this.claims.formFieldCode.read;
                }

                if (this.isAuxRegisterField) {
                    return this.claims.auxiliaryRegisterFieldCode.read;
                }

                if (this.isEventFieldCode) {
                    return this.claims.registerFieldCode.read;
                }

                return false;
            },
            apiUrl() {
                return `/api/dropdown/fieldTemplates?fieldType=${this.model.fieldType}`;
            }
        }
    }
</script>

<style scoped>
    .vs-input {
        width: 100%;
    }

    ::v-deep {
        .vs-dialog {
            min-width: 800px !important;
            height: auto;
        }
    }
</style>
